import React, { useState } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 40px 0;
`;

const Container = styled.div.attrs({ className: 'app-container' })`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: var(--accent-color);
    margin: 15px auto 0;
  }
`;

const ContentContainer = styled.div`
  background-color: var(--light-background);
  padding: 30px;
  border-radius: 8px;
  max-width: 800px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  h2 {
    margin-bottom: 20px;
    color: var(--accent-color);
  }
  
  p {
    margin-bottom: 15px;
    line-height: 1.7;
  }
`;

const InfoItem = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 15px;
    color: var(--accent-color);
  }
`;

const FormContainer = styled.div`
  h2 {
    margin-bottom: 20px;
    color: var(--accent-color);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
  min-height: 150px;
`;

const SubmitButton = styled.button.attrs({
  className: 'contact-submit-button',
  type: 'submit'
})`
  background-color: var(--accent-color);
  color: var(--primary-color);
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #c89a3e;
  }
  
  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        submitted: false,
        error: true,
        message: 'Please fill in all required fields.'
      });
      return;
    }

    // Simulate API call
    setFormStatus({
      submitted: true,
      error: false,
      message: 'Thank you for your message! We will get back to you soon.'
    });

    // Clear form
    setFormData({
      name: '',
      email: '',
      subject: 'general',
      message: ''
    });
  };

  return (
    <PageContainer>
      <Container>
        <SectionTitle>Contact Us</SectionTitle>

        <ContentContainer>
          <ContactGrid>
            <ContactInfo>
              <h2>Get In Touch</h2>
              <p>
                Have questions about our star maps? Need help with your order?
                Or want to report a bug? We'd love to hear from you!
              </p>

              <InfoItem>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <div>
                  <strong>Email</strong>
                  <p>support@interstellarmap.com</p>
                </div>
              </InfoItem>

              <InfoItem>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                <div>
                  <strong>Phone</strong>
                  <p>+1 (555) 123-4567</p>
                </div>
              </InfoItem>

              <InfoItem>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <div>
                  <strong>Office</strong>
                  <p>123 Stargazer Lane, Universe City, CA 90210</p>
                </div>
              </InfoItem>
            </ContactInfo>

            <FormContainer>
              <h2>Send Us a Message</h2>

              {formStatus.submitted && !formStatus.error && (
                <SuccessMessage>{formStatus.message}</SuccessMessage>
              )}

              {formStatus.error && (
                <ErrorMessage>{formStatus.message}</ErrorMessage>
              )}

              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">Your Email *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="subject">Subject</Label>
                  <Select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                  >
                    <option value="general">General Inquiry</option>
                    <option value="support">Customer Support</option>
                    <option value="bug">Report a Bug</option>
                    <option value="feature">Feature Request</option>
                    <option value="other">Other</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="message">Your Message *</Label>
                  <TextArea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                  />
                </FormGroup>

                <div className="form-group">
                  <SubmitButton disabled={formStatus.submitted}>
                    {formStatus.submitted ? 'Sending...' : 'Send Message'}
                  </SubmitButton>
                </div>
              </form>
            </FormContainer>
          </ContactGrid>
        </ContentContainer>
      </Container>
    </PageContainer>
  );
};

export default ContactPage; 